import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeviceInstructions from 'content/modals/AddDeviceModal/DeviceInstructions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';

import AddDevicesSuccess from './AddDevicesSuccess';
import AddDevicesToOrganization from './AddDevicesToOrganization';
import AddNewDevice from './AddNewDevice';

type AddDevicesToOrganizationStage =
  | 'devices'
  | 'addDevice'
  | 'instructions'
  | 'success';

interface AddDevicesToOrganizationModalState {
  stage: AddDevicesToOrganizationStage;
  devices: number[];
  newDeviceSerial?: string;
}

export interface AddDevicesToOrganizationModalStageProps {
  open: boolean;
  state: AddDevicesToOrganizationModalState;
  setState: Dispatch<SetStateAction<AddDevicesToOrganizationModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  organizationId: number | null;
  selectedDevices: number[];
}

const StageComponents: Record<
  AddDevicesToOrganizationStage,
  FC<AddDevicesToOrganizationModalStageProps>
> = {
  devices: AddDevicesToOrganization,
  addDevice: AddNewDevice,
  instructions: DeviceInstructions,
  success: AddDevicesSuccess,
};

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  selectedDevices: number[];
}

const AddDevicesToOrganizationModal: FC<Props> = ({
  onClose,
  open,
  organizationId,
  selectedDevices,
}) => {
  const [state, setState] = useState<AddDevicesToOrganizationModalState>({
    stage: 'devices',
    devices: [],
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, email: '', role: '', devices: [] }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (state.stage === 'devices') {
      setState(prev => ({
        ...prev,
        devices: [],
        addDevice: '',
      }));
      onClose();
    } else if (state.stage === 'addDevice') {
      setState(prev => ({
        ...prev,
        stage: 'devices',
      }));
    } else if (state.stage === 'instructions') {
      setState(prev => ({
        ...prev,
        stage: 'addDevice',
      }));
    }
  }, [onClose, state.stage]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'devices' ? <ArrowBackRoundedIcon /> : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <StageComponent
        open={open}
        setState={setState}
        state={state}
        onClose={onClose}
        onCloseProxy={onCloseProxy}
        organizationId={organizationId}
        selectedDevices={selectedDevices}
      />
    </Modal>
  );
};

export default AddDevicesToOrganizationModal;
